'use client';

import { createClient } from '@/lib/supabase/client';

export interface GameEntry {
  id: string;
  name: string;
  email: string;
  phone: string;
  company: string;
  guess: number | null;
  gamesPlayed: string[];
  manateeTimeMs: number | null;
  manateeCorrect: boolean | null;
  timestamp: string;
  repName?: string | null;
  productInterest?: string[] | null;
  repNotes?: string | null;
  trialEapOptin?: boolean;
  raffleCode?: string | null;
  photoUrl?: string | null;
  emailBounced?: boolean;
}

function toGameEntry(row: any): GameEntry {
  return {
    id: row.id,
    name: row.name,
    email: row.email,
    phone: row.phone,
    company: row.company,
    guess: row.guess ?? null,
    gamesPlayed: row.games_played ?? [],
    manateeTimeMs: row.manatee_time_ms ?? null,
    manateeCorrect: row.manatee_correct ?? null,
    timestamp: row.created_at,
    repName: row.rep_name ?? null,
    productInterest: row.product_interest ?? null,
    repNotes: row.rep_notes ?? null,
    trialEapOptin: row.trial_eap_optin ?? false,
    raffleCode: row.raffle_code ?? null,
    photoUrl: row.photo_url ?? null,
    emailBounced: row.email_bounced ?? false,
  };
}

/** Generate a unique raffle code like "RAF-A3X9K2" */
function generateRaffleCode(): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let code = 'RAF-';
  for (let i = 0; i < 6; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
}

export const entriesService = {
  async checkEmailExists(email: string): Promise<boolean> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('game_entries')
      .select('id')
      .ilike('email', email.trim())
      .limit(1);

    if (error) throw new Error(error.message);
    return (data ?? []).length > 0;
  },

  async createEntry(data: {
    name: string;
    email: string;
    phone: string;
    company: string;
    trialEapOptin?: boolean;
    raffleOptin?: boolean;
  }): Promise<{ id: string; raffleCode: string }> {
    const supabase = createClient();
    const raffleCode = generateRaffleCode();

    const { data: row, error } = await supabase
      .from('game_entries')
      .insert({
        name: data.name,
        email: data.email,
        phone: data.phone,
        company: data.company,
        guess: null,
        games_played: [],
        trial_eap_optin: data.trialEapOptin ?? false,
        raffle_optin: data.raffleOptin ?? false,
        raffle_code: raffleCode,
      })
      .select('id, raffle_code')
      .single();

    if (error) throw new Error(error.message);
    return { id: row.id, raffleCode: row.raffle_code };
  },

  async updateGuess(entryId: string, guess: number): Promise<void> {
    const supabase = createClient();
    const { error } = await supabase
      .from('game_entries')
      .update({ guess })
      .eq('id', entryId);

    if (error) throw new Error(error.message);
  },

  async updateGamesPlayed(entryId: string, gamesPlayed: string[]): Promise<void> {
    const supabase = createClient();
    const { error } = await supabase
      .from('game_entries')
      .update({ games_played: gamesPlayed })
      .eq('id', entryId);

    if (error) throw new Error(error.message);
  },

  async updateManateeResult(
    entryId: string,
    gamesPlayed: string[],
    timeMs: number | null,
    correct: boolean
  ): Promise<void> {
    const supabase = createClient();
    const { error } = await supabase
      .from('game_entries')
      .update({
        games_played: gamesPlayed,
        manatee_time_ms: correct && timeMs !== null ? timeMs : null,
        manatee_correct: correct,
      })
      .eq('id', entryId);

    if (error) throw new Error(error.message);
  },

  async saveSalesRepData(
    entryId: string,
    data: {
      repName: string;
      productInterest: string[];
      notes: string;
    }
  ): Promise<void> {
    const supabase = createClient();
    const { error } = await supabase
      .from('game_entries')
      .update({
        rep_name: data.repName,
        product_interest: data.productInterest,
        rep_notes: data.notes || null,
      })
      .eq('id', entryId);

    if (error) throw new Error(error.message);
  },

  async uploadPlayerPhoto(entryId: string, blob: Blob): Promise<string> {
    const supabase = createClient();
    const fileName = `${entryId}-${Date.now()}.jpg`;

    const { error: uploadError } = await supabase.storage
      .from('player-photos')
      .upload(fileName, blob, { contentType: 'image/jpeg', upsert: true });

    if (uploadError) throw new Error(uploadError.message);

    const { data: urlData } = supabase.storage
      .from('player-photos')
      .getPublicUrl(fileName);

    const publicUrl = urlData.publicUrl;

    const { error: updateError } = await supabase
      .from('game_entries')
      .update({ photo_url: publicUrl })
      .eq('id', entryId);

    if (updateError) throw new Error(updateError.message);

    return publicUrl;
  },

  async getAllEntries(): Promise<GameEntry[]> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('game_entries')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw new Error(error.message);
    return (data ?? []).map(toGameEntry);
  },

  async deleteEntries(ids: string[]): Promise<void> {
    const supabase = createClient();
    const { error } = await supabase
      .from('game_entries')
      .delete()
      .in('id', ids);

    if (error) throw new Error(error.message);
  },
};
